/**
 * `/marcar` — pantalla de marcación con VPP (004 · US1 · T029; ampliada en 006 · T027 · US1,
 * research R-17, FR-005).
 *
 * Ruta file-based de TanStack Router (implementation-notes 002 §4): no se edita `router.tsx` para
 * añadirla. Es una ruta **autenticada**; si la sesión o el dispositivo no sirven, el cliente API
 * emite su evento y el puente de `routes/__root.tsx` navega solo a la pantalla de acceso que toque.
 *
 * Antes de abrir la cámara se consulta `GET /attendance/state` (FR-001): la acción propuesta la
 * decide **siempre** el servidor, y aquí solo se muestra y se reenvía en `POST /challenges` para
 * que el servidor pueda detectar una discrepancia (FR-007). El cliente **no decide** nunca si toca
 * entrada o salida.
 *
 * Con `state = 'UNRESOLVED'` no se abre la cámara: se muestra el mensaje del catálogo y se vuelve
 * al inicio (FR-010).
 *
 * 006 · T027: el Inicio llega con `?accion=CHECK_IN|CHECK_OUT` (la acción que mostraba su botón).
 * Si la respuesta **fresca** de `GET /attendance/state` trae una acción distinta —el Inicio quedó
 * desactualizado por una corrección del administrador mientras estaba abierto—, se muestra la hoja
 * "Tu próxima marcación cambió" **antes** de abrir la cámara, con la acción
 * correcta como botón primario. Sin `?accion=` (deep link directo) no hay hoja: se confía en la
 * acción fresca del servidor sin comparar contra nada.
 *
 * 006 · T065: una vez montado `MarkingFlow`, la acción queda **fija** para ese flujo. Al terminar
 * con éxito, `use-marking` invalida `attendance-state` (para que Inicio llegue refrescado) y esta
 * misma consulta vuelve con la acción **siguiente**: sin el candado, la ruta pintaba "Tu próxima
 * marcación cambió" encima del éxito o reiniciaba el flujo, y el regreso
 * automático a Inicio nunca llegaba. Lo mismo con un refresco por foco a mitad de la marcación.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import type { AttendanceAction } from '@fermin/contracts';

import { Button } from '../components/ui/button';
import { Screen } from '../components/ui/screen';
import { Sheet } from '../components/ui/sheet';
import { SpinnerRing } from '../components/ui/spinner-ring';
import { useAttendanceState } from '../features/attendance/api';
import { MarkingFlow } from '../features/attendance/marking-screen';
import { t } from '../i18n';

export interface MarkingSearch {
  accion?: AttendanceAction;
}

function isAttendanceAction(value: unknown): value is AttendanceAction {
  return value === 'CHECK_IN' || value === 'CHECK_OUT';
}

export function MarkingRoute() {
  const navigate = useNavigate();
  const { accion } = Route.useSearch();
  const state = useAttendanceState();
  const [actionChangeConfirmed, setActionChangeConfirmed] = useState(false);
  /** Acción con la que arrancó `MarkingFlow`; desde ahí los refrescos del estado no la cambian. */
  const [flowAction, setFlowAction] = useState<AttendanceAction | null>(null);

  const goHome = () => {
    void navigate({ to: '/', replace: true });
  };

  if (flowAction !== null) {
    return <MarkingFlow proposedAction={flowAction} onExit={goHome} />;
  }

  // R-17: la acción se compara contra una respuesta **fresca**. Al llegar desde Inicio la consulta
  // ya tiene en caché el estado que pintó el botón; hasta que termine la petición de esta visita no
  // se decide nada (ni la hoja de acción cambiada ni el candado del flujo).
  if (state.isPending || !state.isFetchedAfterMount) {
    return (
      <Screen data-testid="marking-loading" align="center">
        <div className="flex flex-col items-center gap-fm-3">
          <SpinnerRing label={t('common.states.loading')} />
          <p aria-live="polite" className="text-fm-base text-fm-text-secondary">
            {t('common.states.loading')}
          </p>
        </div>
      </Screen>
    );
  }

  if (state.isError) {
    // `401`/`403`/`423` ya los enrutó el puente de eventos; lo que queda es un fallo general.
    return (
      <Screen
        data-testid="marking-state-error"
        align="center"
        title={t('attendance.error.title')}
        lead={state.error.message}
        footer={
          <Button variant="secondary" onClick={goHome}>
            {t('attendance.actions.backHome')}
          </Button>
        }
      />
    );
  }

  if (state.data.state === 'UNRESOLVED' || state.data.proposed_action === null) {
    return (
      <Screen
        data-testid="marking-unresolved"
        align="center"
        title={t('attendance.error.title')}
        lead={t('common.errors.ATTENDANCE_STATE_UNRESOLVED')}
        footer={
          <Button variant="secondary" onClick={goHome}>
            {t('attendance.actions.backHome')}
          </Button>
        }
      />
    );
  }

  const proposedAction = state.data.proposed_action;
  const actionChanged = accion !== undefined && accion !== proposedAction && !actionChangeConfirmed;

  if (actionChanged) {
    const label =
      proposedAction === 'CHECK_IN' ? t('attendance.actions.checkIn') : t('attendance.actions.checkOut');
    return (
      <Screen data-testid="marking-action-changed-before-screen" align="center">
        <Sheet
          open
          dismissible={false}
          onClose={goHome}
          title={t('attendance.actionChanged.title')}
          description={t('common.errors.ACTION_CHANGED')}
          footer={
            <>
              <Button
                data-testid="marking-action-changed-before-confirm"
                onClick={() => setActionChangeConfirmed(true)}
              >
                {label}
              </Button>
              <Button variant="ghost" onClick={goHome}>
                {t('attendance.actions.backHome')}
              </Button>
            </>
          }
        >
          <span data-testid="marking-action-changed-before" className="sr-only" />
        </Sheet>
      </Screen>
    );
  }

  // Candado del flujo (patrón de React "ajustar estado durante el render": se fija una sola vez).
  setFlowAction(proposedAction);
  return <MarkingFlow proposedAction={proposedAction} onExit={goHome} />;
}

export const Route = createFileRoute('/marcar')({
  validateSearch: (search: Record<string, unknown>): MarkingSearch =>
    isAttendanceAction(search.accion) ? { accion: search.accion } : {},
  component: MarkingRoute,
});
